import React, { PureComponent } from "react";
import { Modal, Button } from "react-bootstrap";
import EventEmitter from "../../services/event-emitter";
import { getRequest } from "../../api/ApiService";
import {
  logError,
  hexToRgba,
  addUserSpecificParams,
  ucfirst
} from "../../services/utils";
import { Scrollbars } from "react-custom-scrollbars";
import TypeformUrls from "../typeform/TypeformUrls";
import Radium, { StyleRoot } from "radium";
import { fadeIn } from "react-animations";
import TypeformModal from "../typeform/modal";

const fadeInStyle = {
  animation: "x 1s",
  animationName: Radium.keyframes(fadeIn, "fadeIn")
};

const FEEDBACK_CONTENT = {
  "market leader":
    "Bold, fearless, dominant, are a few words that come into mind. Keep performing at this high level and maintain your leadership. <b>#Boss</b>!",
  "market challenger":
    "Great round! You've done a great job in challenging the leader. Next round, become the leader. <b>#Soon-to-be-Boss</b>",
  "market climber":
    "Not bad! Keep climbing until you reach your full potential. <b>#Be-the-Boss</b>",
  "market warrior":
    "Good battle! You fought the good fight. Now let's go win the war. Become the leader! <b>#Be-the-Boss</b>",
  "market newbie":
    "Young Grasshopper, you have ways to go, but we will help you climb, to become a warrior to then challenge the leader. You will be a Master. <b>#Be-the-Boss</b>"
};

const FEEDBACK_STEP_RANK_INTRO = 1;
const FEEDBACK_STEP_HELP_INTRO = 2;
const FEEDBACK_STEP_NO_SALES = 3;
const FEEDBACK_STEP_LOW_SALES = 4;
const FEEDBACK_STEP_LOW_GROSS_PROFITS = 5;
const FEEDBACK_STEP_LOW_NET_PROFITS = 6;
const FEEDBACK_STEP_OTHER_FACTORS = 7;
const FEEDBACK_STEP_CLOSING = 8;

const FEEDBACK_APOSTROPHE = "'";

class FeedbackModal extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      show: false,
      step: FEEDBACK_STEP_RANK_INTRO,
      data: {}
    };

    this.eventemitter = EventEmitter.getInstance();
  }

  componentDidMount() {
    this.eventemitter.on("feedback-modal-show", this.show);
  }

  componentWillUnmount() {
    this.eventemitter.off("feedback-modal-show", this.show);
  }

  typeformLink = key => {
    return addUserSpecificParams(TypeformUrls[key]);
  };

  fetchData = () => {
    const { round_number } = this.props;

    getRequest(
      `${process.env.REACT_APP_API_URL}teams/messages/?round_number=${round_number}`
    )
      .then(res => {
        let data = {};

        res.body.forEach(item => {
          data[item.type_name] = item.message;
        });

        if ("no_products_accepted" in data) {
          let val = data["no_products_accepted"];
          val = val.replace("[", "").replace("]");
          val = val.toLowerCase() === "true" ? true : false;
          data["no_products_accepted"] = val;
        }

        this.setState({
          data
        });
      })
      .catch(err => {
        logError(err, "FeedbackModal", "fetchData");
      });
  };

  show = () => {
    this.setState(
      {
        show: true,
        step: FEEDBACK_STEP_RANK_INTRO
      },
      () => {
        this.fetchData();
      }
    );
  };

  hide = () => {
    this.setState({
      show: false
    });
  };

  openTypeformModal = e => {
    const url = e.target.href;

    e.preventDefault();
    this.eventemitter.trigger("open-typeform-modal", [url]);
  };

  content = () => {
    const { rank_name = "" } = this.props;
    const { step } = this.state;

    if (
      step === FEEDBACK_STEP_RANK_INTRO &&
      rank_name !== "" &&
      rank_name.toLowerCase() in FEEDBACK_CONTENT
    ) {
      return (
        <StyleRoot>
          <div
            className="feedback-text"
            dangerouslySetInnerHTML={{
              __html: FEEDBACK_CONTENT[rank_name.toLowerCase()]
            }}
            style={fadeInStyle}
          />
        </StyleRoot>
      );
    } else if (step === FEEDBACK_STEP_HELP_INTRO) {
      return this.helpIntroContent;
    } else if (step === FEEDBACK_STEP_NO_SALES) {
      return this.noSalesContent;
    } else if (step === FEEDBACK_STEP_LOW_SALES) {
      return this.lowCostsContent;
    } else if (step === FEEDBACK_STEP_LOW_GROSS_PROFITS) {
      return this.lowGrossProfitsContent;
    } else if (step === FEEDBACK_STEP_LOW_NET_PROFITS) {
      return this.lowNetProfitsContent;
    } else if (step === FEEDBACK_STEP_OTHER_FACTORS) {
      return this.otherFactorsContent;
    } else if (step === FEEDBACK_STEP_CLOSING) {
      return this.closingContent;
    }

    return "";
  };

  get helpIntroContent() {
    return (
      <StyleRoot>
        <div className="feedback-text" style={fadeInStyle}>
          Your ranking is heavily driven by your profitability and your profits
          are reported on the <b>Income Statement</b>, which breaks down your
          revenues and expenses.
          <br />
          <br />
          There are 3 key sections that could be adversely impacting your Net
          profits:
          <br />
          <br />
          1. Sales (i.e. Revenues) section
          <br />
          2. Gross Profit section
          <br />
          3. Net Profit section
          <br />
          <br />
          Let{FEEDBACK_APOSTROPHE}s go through each section to help you identify
          where your challenges might be.
        </div>
      </StyleRoot>
    );
  }

  get noSalesContent() {
    return (
      <StyleRoot>
        <div className="feedback-text" style={fadeInStyle}>
          <b>
            <u>NO SALES WERE GENERATED</u>
          </b>
          <br />
          <br />
          Your team didn{FEEDBACK_APOSTROPHE}t sell any units because all
          department decisions were not submitted. It is a requirement in order
          to compete. If your team made decisions, and did not submit them, then
          those decisions would be treated as if they never occurred. Please
          ensure that either your department heads and/or your CEO submits all
          decisions.
        </div>
      </StyleRoot>
    );
  }

  get lowCostsContent() {
    const { data } = this.state;
    const { not_sold_allocation } = data;
    const products_names = Object.keys(not_sold_allocation);

    const mobile_sentenses = [];
    let auto_sentenses = [];
    let mobile_demand = null;
    let auto_demand = null;
    products_names.forEach(product_name => {
      const { Auto, Mobile, auto_value, mobile_value } = not_sold_allocation[
        product_name
      ][0];

      if (mobile_demand === null) {
        mobile_demand = (mobile_value / Mobile / 1000000).toFixed(2);
      }

      if (auto_demand === null) {
        auto_demand = (auto_value / Auto / 1000000).toFixed(2);
      }

      const mobile_non_allocation = ((1 - Mobile) * 100).toFixed(2);
      const auto_non_allocation = ((1 - Auto) * 100).toFixed(2);

      mobile_sentenses.push(
        `${ucfirst(
          product_name
        )} selling potential: ${mobile_non_allocation}% of mobile demand.<br />`
      );
      auto_sentenses.push(
        `${ucfirst(
          product_name
        )} selling potential: ${auto_non_allocation}% of auto demand.<br />`
      );
    });

    let products_list = [];
    if (
      "no_submissions" in data &&
      "no_products_accepted" in data &&
      data["no_products_accepted"] === false
    ) {
      products_list = Object.keys(data["no_submissions"]);
    }

    return (
      <StyleRoot>
        <div className="feedback-text" style={fadeInStyle}>
          <b>
            <u>LOW SALES</u>
          </b>
          <br />
          <br />
          Your sales could have been your downfall.
          <br />
          <br />
          {products_list.length > 0 && (
            <span>
              Product updates for the following products were not submitted and
              therefore customers would not be aware -{" "}
              {products_list.join(", ")}.
              <br />
              <br />
            </span>
          )}
          Given your Marketing Mix, you may have Product Market Fit challenges.
          This would result in lower sales because only a limited number of
          customers would even consider buying your products, limiting your
          selling potential. To improve your chances of becoming Market Leaders,
          your selling potential, for at least one of the segments, should be at
          least 50%, which is industry average. Here are both segments
          breakdown:
          <br />
          <br />
          <b>Mobile Demand - {mobile_demand}M units.</b>
          <br />
          <span
            dangerouslySetInnerHTML={{ __html: mobile_sentenses.join("") }}
          />
          <br />
          <b>Auto Demand - {auto_demand}M units.</b>
          <br />
          <span dangerouslySetInnerHTML={{ __html: auto_sentenses.join("") }} />
          <br />
          If consideration was less than 50%, we recommend you review the
          following content from our Knowledge Base:
          <br />
          <br />
          1.{" "}
          <a href={this.typeformLink("STP")} onClick={this.openTypeformModal}>
            STP
          </a>
          <br />
          2.{" "}
          <a
            href={this.typeformLink("MARKETING_MIX")}
            onClick={this.openTypeformModal}
          >
            Marketing Mix
          </a>
          <br />
          3.{" "}
          <a
            href={this.typeformLink("PRODUCT_MARKET_FIT")}
            onClick={this.openTypeformModal}
          >
            Product Market Fit
          </a>
          <br />
          4.{" "}
          <a
            href={this.typeformLink("MARKETING_FUNNEL")}
            onClick={this.openTypeformModal}
          >
            Marketing Funnel
          </a>
        </div>
      </StyleRoot>
    );
  }

  get lowGrossProfitsContent() {
    return (
      <StyleRoot>
        <div className="feedback-text" style={fadeInStyle}>
          <b>
            <u>LOW GROSS PROFITS MARGINS</u>
          </b>
          <br />
          <br />
          Having low gross profits margins is not sexy. It{FEEDBACK_APOSTROPHE}s
          not in the Market Leaders{FEEDBACK_APOSTROPHE} DNA. In this game, the
          industry average gross profit margin is <b>50%</b>. Please review your
          financials and ensure that your gross profit margins are{" "}
          <b>at least 50%</b>.<br />
          <br />
          Anything below that will make it challenging for you to compete
          successfully. To improve your gross margins, we recommend you read the
          following from our Knowledge Base:
          <br />
          <br />
          1.{" "}
          <a
            href={this.typeformLink("INCOME_STATEMENT")}
            onClick={this.openTypeformModal}
          >
            Income Statement
          </a>
          <br />
          2.{" "}
          <a
            href={this.typeformLink("PRODUCT_COSTS")}
            onClick={this.openTypeformModal}
          >
            Product Costs
          </a>
          <br />
          3.{" "}
          <a
            href={this.typeformLink("UTILIZATION")}
            onClick={this.openTypeformModal}
          >
            Utilization
          </a>
        </div>
      </StyleRoot>
    );
  }

  get lowNetProfitsContent() {
    return (
      <StyleRoot>
        <div className="feedback-text" style={fadeInStyle}>
          <b>
            <u>LOW NET PROFITS MARGINS</u>
          </b>
          <br />
          <br />
          Your Net Profits is a key driver of your team score and ranking.
          Therefore, earning the highest Net Profit Margins (i.e. Net
          Profits/Sales) would better your odds in becoming the Market Leader.
          Please review your <b>R&D</b> (i.e. Product Development costs) and{" "}
          <b>promotion and sales costs</b> (i.e. Product Marketing costs) on
          your <b>Income Statement</b>. They eat way at your Net Profits.
          <br />
          <br />
          We also notice that you have a high debt to capital ratio, which could
          be impacting your Net Profit Margin Potential.{" "}
          <u>High Debt to capital ratio = High Debt = High Financing Costs</u>.
          Financing costs eats away at your Net Profits. Your financing costs is
          labeled as <b>Interest Expense</b> on your Income Statement.
          <br />
          <br />
          To better your chances of increasing your net profits, we recommend
          that you review the following content in our Knowledge Base:
          <br />
          <br />
          1.{" "}
          <a
            href={this.typeformLink("INCOME_STATEMENT")}
            onClick={this.openTypeformModal}
          >
            Income Statement
          </a>
          <br />
          2.{" "}
          <a
            href={this.typeformLink("DEBT_TO_CAPITAL")}
            onClick={this.openTypeformModal}
          >
            Debt to Capital
          </a>
        </div>
      </StyleRoot>
    );
  }

  get otherFactorsContent() {
    return (
      <StyleRoot>
        <div className="feedback-text" style={fadeInStyle}>
          <b>
            <u>OTHER FACTORS</u>
          </b>
          <br />
          <br />
          Your performance could have been impacted by other factors. We
          recommend that you review the following content in our Knowledge Base:
          <br />
          <br />
          1.{" "}
          <a
            href={this.typeformLink("COST_OF_CAPITAL")}
            onClick={this.openTypeformModal}
          >
            High Cost of Capital
          </a>
          <br />
          2.{" "}
          <a
            href={this.typeformLink("CAPITAL_BUDGETING")}
            onClick={this.openTypeformModal}
          >
            High Capex Spending
          </a>
          <br />
          3.{" "}
          <a
            href={this.typeformLink("SHARES_OUTSTANDING")}
            onClick={this.openTypeformModal}
          >
            High Share Issuance
          </a>
        </div>
      </StyleRoot>
    );
  }

  get closingContent() {
    return (
      <StyleRoot>
        <div className="feedback-text" style={fadeInStyle}>
          That{FEEDBACK_APOSTROPHE}s all for now! Please review what we
          {FEEDBACK_APOSTROPHE}ve recommended and all the best in the next
          round!!!
        </div>
      </StyleRoot>
    );
  }

  get steps() {
    const { data } = this.state;
    const { rank_name = "" } = this.props;

    let steps = [FEEDBACK_STEP_RANK_INTRO];

    let show_help = false;
    if (
      rank_name !== "" &&
      ["market warrior", "market climber", "market newbie"].includes(
        rank_name.toLowerCase()
      )
    ) {
      show_help = true;
    }

    if (show_help === true) {
      steps.push(FEEDBACK_STEP_HELP_INTRO);

      if (
        "no_submissions" in data &&
        "no_products_accepted" in data &&
        data["no_products_accepted"] === true
      ) {
        steps.push(FEEDBACK_STEP_NO_SALES);
      } else {
        if ("not_sold_allocation" in data) {
          steps.push(FEEDBACK_STEP_LOW_SALES);
        }

        if (data.fs_results.low_gross_profit === true) {
          steps.push(FEEDBACK_STEP_LOW_GROSS_PROFITS);
        }

        if (data.fs_results.low_net_profits === true) {
          steps.push(FEEDBACK_STEP_LOW_NET_PROFITS);
        }

        if (
          data.fs_results.high_cost_to_capital === true ||
          data.fs_results.high_capex === true ||
          data.fs_results.high_share_issuance > 0
        ) {
          steps.push(FEEDBACK_STEP_OTHER_FACTORS);
        }
      }
    }

    steps.push(FEEDBACK_STEP_CLOSING);

    return steps;
  }

  prev = () => {
    const { step } = this.state;
    const steps = this.steps;
    const index = steps.indexOf(step);

    if (index > 0) {
      this.setState({
        step: steps[index - 1]
      });
    }
  };

  next = () => {
    const { step } = this.state;
    const steps = this.steps;
    const index = steps.indexOf(step);
    const lastIndex = steps.length - 1;

    if (index >= 0) {
      if (index === lastIndex) {
        this.hide();
      } else {
        this.setState({
          step: steps[index + 1]
        });
      }
    }
  };

  renderThumbVertical = ({ style, ...props }) => {
    const { team_color = "#fff" } = this.props;

    return (
      <div
        {...props}
        style={{
          ...style,
          backgroundColor: hexToRgba(team_color, 0.6),
          borderRadius: "5px"
        }}
        className="thumb-vertical"
      />
    );
  };

  render() {
    const {
      rank_name = "",
      team_color = "#fff",
      round_number = 0
    } = this.props;
    const { show, step } = this.state;

    const captionStyle = {
      color: team_color
    };
    const scrollBarsSettings = {
      style: {
        height: "calc(100% - 60px)"
      },
      renderThumbVertical: this.renderThumbVertical
    };

    return (
      <Modal
        show={show}
        onHide={this.hide}
        dialogClassName="feedback-modal"
        backdrop={true}
        backdropClassName="feedback-backdrop"
      >
        <Modal.Body>
          <Scrollbars {...scrollBarsSettings}>
            <div className="feedback-text feedback-text-top">
              For Round {round_number}, you and your team are
            </div>
            <div className="feedback-caption" style={captionStyle}>
              {rank_name}s!!!
            </div>
            {this.content()}
          </Scrollbars>
          <div className="feedback-button">
            {step !== FEEDBACK_STEP_RANK_INTRO && (
              <Button bsStyle="primary" bsSize="lg" onClick={this.prev}>
                Previous
              </Button>
            )}
            <Button bsStyle="primary" bsSize="lg" onClick={this.next}>
              {step === FEEDBACK_STEP_CLOSING ? "Close" : "Next"}
            </Button>
          </div>
          <TypeformModal />
        </Modal.Body>
      </Modal>
    );
  }
}

export default FeedbackModal;



// WEBPACK FOOTER //
// ./src/components/feedback/modal.js